import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from '../components/Toast';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import { useChannelsOperational } from '../hooks/useChannelOperational';
import { useTranslation } from 'react-i18next';
import CuentaConectada from '../componentes/integraciones/CuentaConectada';

const Integrations = () => {
  const { context } = useOrganization();
  // Operational badge for ig/tiktok (helper, tri-state). gmail is NOT messaging-capable →
  // stays on its singleton below. The singleton listener also stays (accountName/connectedAt
  // + disconnect button) — the helper only answers "operational?", the singleton the detail.
  const operational = useChannelsOperational(['instagram', 'tiktok']);
  const [integrations, setIntegrations] = useState({
    instagram: { connected: false, loading: false },
    tiktok: { connected: false, loading: false },
    gmail: { connected: false, loading: false }
  });
  const { t } = useTranslation('channels');
  const navigate = useNavigate();

  // H-70/H-71 — la vuelta de OAuth trae `?error=...` y esta página NUNCA lo leía: todas las
  // redirecciones de error del callback (`gmail_scope_missing`, `token_exchange_failed`,
  // `state_expired`, y ahora `gmail_no_refresh_token`) aterrizaban en silencio. El usuario
  // veía la pantalla de integraciones sin conectar y sin explicación, así que reintentaba
  // y le volvía a pasar lo mismo. Copia exacta de la forma que ya funciona en
  // `Canales.jsx:109-121`, incluido el `defaultValue` para códigos sin traducción.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (!err) return;
    toast.error(
      t(`oauthErrors.${err}`, { defaultValue: t('oauthErrors.generico', { codigo: err }) }),
      // El Toast del repo toma (mensaje, duracionMs). Un objeto haría setTimeout(fn, {}) y
      // el aviso desaparecería al instante — justo el que hay que leer entero.
      15000
    );
    // Se limpia la query para que un refresco no repita el aviso.
    window.history.replaceState({}, '', window.location.pathname);
  }, [t]);

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    // Listen to integration status in real-time
    const unsubscribers = [];

    ['instagram', 'tiktok', 'gmail'].forEach(provider => {
      // Query based on organization context
      let integrationRef;
      if (context.mode === 'organization' && context.orgId) {
        // Org mode: query from organizations/{orgId}/integrations
        integrationRef = doc(db, 'organizations', context.orgId, 'integrations', provider);
      } else {
        // Personal mode: query from clientes/{uid}/integrations
        integrationRef = doc(db, 'clientes', auth.currentUser.uid, 'integrations', provider);
      }

      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (!doc.exists()) {
          setIntegrations(prev => ({
            ...prev,
            [provider]: {
              ...prev[provider],
              connected: false,
              loading: false
            }
          }));
          return;
        }

        const data = doc.data();

        // No need to validate organizationId - the path itself determines the context
        setIntegrations(prev => ({
          ...prev,
          [provider]: {
            ...prev[provider],
            ...data,
            connected: data.connected === true,
            loading: false
          }
        }));
      });
      unsubscribers.push(unsubscribe);
    });

    return () => unsubscribers.forEach(unsub => unsub());
  }, [navigate, context.mode, context.orgId]);

  const handleConnect = async (provider) => {
    setIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], loading: true }
    }));

    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();

      // ✅ Incluir organizationId en el request
      const organizationId = context.mode === 'organization' ? context.orgId : null;

      // Use new API routes
      const response = await fetch(`/api/oauth/${provider}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Org-Id': organizationId || '' // Header con org
        },
        body: JSON.stringify({
          organizationId // Body con org para que llegue al state
        })
      });

      if (!response.ok) throw new Error('Failed to start OAuth flow');

      const { authUrl } = await response.json();
      window.location.href = authUrl;
    } catch (error) {
      console.error(`Error connecting to ${provider}:`, error);
      toast.error(t('integrations.errorConnect', { provider }));
      setIntegrations(prev => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false }
      }));
    }
  };

  const handleDisconnect = async (provider) => {
    const providerNames = { instagram: 'Instagram', tiktok: 'TikTok', gmail: 'Gmail' };
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('integrations.confirmDisconnect', { provider: providerNames[provider] || provider }))) {
      return;
    }

    setIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], loading: true }
    }));

    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();

      // ✅ Incluir organizationId en el request
      const organizationId = context.mode === 'organization' ? context.orgId : null;

      // Use new API routes
      const response = await fetch(`/api/oauth/${provider}/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Org-Id': organizationId || ''
        },
        body: JSON.stringify({
          organizationId
        })
      });

      if (!response.ok) throw new Error('Failed to disconnect');
    } catch (error) {
      console.error(`Error disconnecting ${provider}:`, error);
      toast.error(t('integrations.errorDisconnect', { provider }));
      setIntegrations(prev => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false }
      }));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div style={styles.container}>
      <nav style={styles.breadcrumb}>
        <Link to="/dashboard" style={styles.breadcrumbLink}>🏠 Dashboard</Link> / 
        <span style={styles.breadcrumbCurrent}>{t('integrations.breadcrumb')}</span>
      </nav>

      <div style={styles.header}>
        <h1 style={styles.title}>{t('integrations.title')}</h1>
        <p style={styles.subtitle}>{t('integrations.subtitle')}</p>
      </div>

      <div style={styles.grid}>
        {/* Instagram Card */}
        <div style={{...styles.card, ...styles.instagramCard}}>
          <div style={styles.cardIcon}>📸</div>
          <h2 style={styles.cardTitle}>Instagram</h2>
          <p style={styles.cardDescription}>
            {t('integrations.igDescription')}
          </p>

          {/* Badge operativo del helper (estado=='conectado' + gate); accountName/connectedAt
              + disconnect siguen del singleton (la convivencia justificada: cada fuente lo suyo). */}
          {operational.instagram === undefined ? (
            <div style={styles.disconnectedSection}>
              <div style={styles.statusDisconnected}>
                <span style={styles.xIcon}>⏳</span>
                <span>{t('social.checkingStatus', 'Verificando estado…')}</span>
              </div>
            </div>
          ) : operational.instagram ? (
            <div style={styles.connectedSection}>
              <CuentaConectada
                variante="global"
                cuenta={integrations.instagram.accountName || 'usuario'}
                etiqueta={t('integrations.connectedAs', { account: integrations.instagram.accountName || 'usuario' })}
              />
              {integrations.instagram.connectedAt && (
                <p style={styles.connectionDate}>
                  {t('integrations.connectedSince', { date: formatDate(integrations.instagram.connectedAt) })}
                </p>
              )}
              <button
                onClick={() => handleDisconnect('instagram')}
                disabled={integrations.instagram.loading}
                style={{...styles.button, ...styles.disconnectButton}}
              >
                {integrations.instagram.loading ? t('integrations.disconnecting') : t('integrations.disconnectBtn')}
              </button>
            </div>
          ) : (
            <div style={styles.disconnectedSection}>
              <div style={styles.statusDisconnected}>
                <span style={styles.xIcon}>⭕</span>
                <span>{t('integrations.notConnected')}</span>
              </div>
              <button
                onClick={() => handleConnect('instagram')}
                disabled={integrations.instagram.loading}
                style={{...styles.button, ...styles.connectButton, ...styles.instagramButton}}
              >
                {integrations.instagram.loading ? t('integrations.connecting') : t('integrations.connectProvider', { provider: 'Instagram' })}
              </button>
            </div>
          )}
        </div>

        {/* TikTok Card */}
        <div style={{...styles.card, ...styles.tiktokCard}}>
          <div style={styles.cardIcon}>🎵</div>
          <h2 style={styles.cardTitle}>TikTok</h2>
          <p style={styles.cardDescription}>
            {t('integrations.ttDescription')}
          </p>

          {/* TikTok no-operativo hoy por el gate MESSAGING_CAPABLE → operational.tiktok siempre
              false → rama "no conectado". (Display/disconnect del singleton, igual que IG.) */}
          {operational.tiktok === undefined ? (
            <div style={styles.disconnectedSection}>
              <div style={styles.statusDisconnected}>
                <span style={styles.xIcon}>⏳</span>
                <span>{t('social.checkingStatus', 'Verificando estado…')}</span>
              </div>
            </div>
          ) : operational.tiktok ? (
            <div style={styles.connectedSection}>
              <CuentaConectada
                variante="global"
                cuenta={integrations.tiktok.accountName || 'usuario'}
                etiqueta={t('integrations.connectedAs', { account: integrations.tiktok.accountName || 'usuario' })}
              />
              {integrations.tiktok.connectedAt && (
                <p style={styles.connectionDate}>
                  {t('integrations.connectedSince', { date: formatDate(integrations.tiktok.connectedAt) })}
                </p>
              )}
              <button
                onClick={() => handleDisconnect('tiktok')}
                disabled={integrations.tiktok.loading}
                style={{...styles.button, ...styles.disconnectButton}}
              >
                {integrations.tiktok.loading ? t('integrations.disconnecting') : t('integrations.disconnectBtn')}
              </button>
            </div>
          ) : (
            <div style={styles.disconnectedSection}>
              <div style={styles.statusDisconnected}>
                <span style={styles.xIcon}>⭕</span>
                <span>{t('integrations.notConnected')}</span>
              </div>
              <button
                onClick={() => handleConnect('tiktok')}
                disabled={integrations.tiktok.loading}
                style={{...styles.button, ...styles.connectButton, ...styles.tiktokButton}}
              >
                {integrations.tiktok.loading ? t('integrations.connecting') : t('integrations.connectProvider', { provider: 'TikTok' })}
              </button>
            </div>
          )}
        </div>

        {/* Gmail Card */}
        <div style={{...styles.card, ...styles.gmailCard}}>
          <div style={styles.cardIcon}>📧</div>
          <h2 style={styles.cardTitle}>Gmail</h2>
          <p style={styles.cardDescription}>
            {t('integrations.gmailDescription')}
          </p>

          {integrations.gmail.connected ? (
            <div style={styles.connectedSection}>
              <CuentaConectada
                variante="global"
                cuenta={integrations.gmail.accountName || 'usuario@gmail.com'}
                etiqueta={t('integrations.connectedAsEmail', { account: integrations.gmail.accountName || 'usuario@gmail.com' })}
              />
              {integrations.gmail.connectedAt && (
                <p style={styles.connectionDate}>
                  {t('integrations.connectedSince', { date: formatDate(integrations.gmail.connectedAt) })}
                </p>
              )}
              <button
                onClick={() => handleDisconnect('gmail')}
                disabled={integrations.gmail.loading}
                style={{...styles.button, ...styles.disconnectButton}}
              >
                {integrations.gmail.loading ? t('integrations.disconnecting') : t('integrations.disconnectBtn')}
              </button>
            </div>
          ) : (
            <div style={styles.disconnectedSection}>
              <div style={styles.statusDisconnected}>
                <span style={styles.xIcon}>⭕</span>
                <span>{t('integrations.notConnected')}</span>
              </div>
              <button
                onClick={() => handleConnect('gmail')}
                disabled={integrations.gmail.loading}
                style={{...styles.button, ...styles.connectButton, ...styles.gmailButton}}
              >
                {integrations.gmail.loading ? t('integrations.connecting') : t('integrations.connectProvider', { provider: 'Gmail' })}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.footer}>
        <p style={styles.footerText} dangerouslySetInnerHTML={{ __html: t('integrations.tip') }} />
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '2rem',
    fontSize: '14px'
  },
  breadcrumbLink: {
    textDecoration: 'none',
    color: '#1a73e8',
    fontWeight: '500'
  },
  breadcrumbCurrent: {
    color: '#666',
    marginLeft: '0.5rem'
  },
  header: {
    textAlign: 'center',
    marginBottom: '3rem'
  },
  title: {
    fontSize: '2rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 1rem 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.5'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0',
    textAlign: 'center',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  },
  instagramCard: {
    background: 'linear-gradient(135deg, #f9f9f9 0%, #fafafa 100%)',
    borderTop: '4px solid #e1306c'
  },
  tiktokCard: {
    background: 'linear-gradient(135deg, #f9f9f9 0%, #fafafa 100%)',
    borderTop: '4px solid #000000'
  },
  gmailCard: {
    background: 'linear-gradient(135deg, #f9f9f9 0%, #fafafa 100%)',
    borderTop: '4px solid #d93025'
  },
  cardIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    display: 'block'
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 0.5rem 0'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '2rem',
    lineHeight: '1.5'
  },
  connectedSection: {
    textAlign: 'center'
  },
  disconnectedSection: {
    textAlign: 'center'
  },
  statusConnected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    fontSize: '14px',
    fontWeight: '500',
    color: '#16a34a'
  },
  statusDisconnected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280'
  },
  checkIcon: {
    fontSize: '16px'
  },
  xIcon: {
    fontSize: '16px'
  },
  connectionDate: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '1.5rem'
  },
  button: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '160px'
  },
  connectButton: {
    color: 'white'
  },
  instagramButton: {
    background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    boxShadow: '0 4px 12px rgba(188, 24, 136, 0.3)'
  },
  tiktokButton: {
    backgroundColor: '#000000',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  },
  gmailButton: {
    backgroundColor: '#d93025',
    boxShadow: '0 4px 12px rgba(217, 48, 37, 0.3)'
  },
  disconnectButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
  },
  footer: {
    background: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '12px',
    textAlign: 'center'
  },
  footerText: {
    margin: 0,
    fontSize: '14px',
    color: '#475569',
    lineHeight: '1.6'
  }
};

export default Integrations;